import type { Artist, Booking, StudioId } from '@/domain/types';
import { rebookedInChair } from '@/domain/reportingEngine';

export interface ArtistRow {
  artistId: string;
  name: string;
  studioId: StudioId;
  rebooksThisMonth: number;
  totalCompletedThisMonth: number;
  streak: number;
  badges: string[];
  /**
   * Change in leaderboard position vs 24h ago. Positive = moved UP the list,
   * negative = moved DOWN. `undefined` means the artist wasn't on the
   * leaderboard 24h ago (first rebook landed in the last day).
   */
  movement: number | undefined;
  /** Number of rebooks captured in the last 24h. Drives the small delta hint. */
  rebooksLast24h: number;
}

interface RankingEntry {
  artistId: string;
  name: string;
  studioId: StudioId;
  rebooks: number;
  streak: number;
  totalCompleted: number;
}

/**
 * Builds a ranking from completed bookings filtered by a checkoutCutoff. Anything
 * checked out AFTER the cutoff is excluded — used to compute "as of 24h ago".
 */
function buildRanking(
  bookings: Booking[],
  artists: Record<string, Artist>,
  monthStart: number,
  checkoutCutoff: number,
  filterStudioId?: StudioId,
): RankingEntry[] {
  const rows = new Map<string, RankingEntry>();
  const ensure = (artistId: string): RankingEntry | undefined => {
    const a = artists[artistId];
    if (!a) return undefined;
    if (filterStudioId && a.studioId !== filterStudioId) return undefined;
    let existing = rows.get(artistId);
    if (existing) return existing;
    existing = {
      artistId,
      name: a.displayName || a.firstName,
      studioId: a.studioId,
      rebooks: 0,
      streak: 0,
      totalCompleted: 0,
    };
    rows.set(artistId, existing);
    return existing;
  };

  const sortedDesc = bookings
    .filter((b) => b.status === 'completed' && b.checkedOutAt)
    .filter((b) => new Date(b.checkedOutAt!).getTime() <= checkoutCutoff)
    .sort((a, b) => new Date(b.checkedOutAt!).getTime() - new Date(a.checkedOutAt!).getTime());

  for (const b of sortedDesc) {
    if (new Date(b.checkedOutAt!).getTime() < monthStart) continue;
    const row = ensure(b.artistId);
    if (!row) continue;
    row.totalCompleted += 1;
    if (rebookedInChair(b)) row.rebooks += 1;
  }

  // Streak: walk the descending sequence per artist, count consecutive rebooks
  // from the most recent session.
  const streakState = new Map<string, { current: number; broken: boolean }>();
  for (const b of sortedDesc) {
    if (!rows.has(b.artistId)) continue;
    const state = streakState.get(b.artistId) ?? { current: 0, broken: false };
    if (state.broken) continue;
    if (rebookedInChair(b)) state.current += 1;
    else state.broken = true;
    streakState.set(b.artistId, state);
  }
  for (const [aid, s] of streakState) {
    const row = rows.get(aid);
    if (row) row.streak = s.current;
  }

  return Array.from(rows.values()).sort((a, b) => {
    if (b.rebooks !== a.rebooks) return b.rebooks - a.rebooks;
    return b.streak - a.streak;
  });
}

/**
 * Build the leaderboard. Computes today's ranking and a "24h ago" ranking
 * (excluding any rebook that landed in the last 24h) so each row can carry a
 * movement indicator — how many positions the artist climbed or slipped since
 * yesterday.
 */
export function buildArtistLeaderboard(
  bookings: Record<string, Booking>,
  artists: Record<string, Artist>,
  nowIso: string,
  filterStudioId?: StudioId,
): ArtistRow[] {
  const now = new Date(nowIso);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).getTime();
  const yesterdayCutoff = now.getTime() - 24 * 60 * 60 * 1000;
  const all = Object.values(bookings);

  const today = buildRanking(all, artists, monthStart, now.getTime(), filterStudioId);
  const yesterday = buildRanking(all, artists, monthStart, yesterdayCutoff, filterStudioId);
  const yesterdayPos = new Map(yesterday.map((r, i) => [r.artistId, i]));

  const weekStart = (() => {
    const d = new Date(now);
    d.setDate(d.getDate() - d.getDay());
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  })();

  return today.map((row, idx) => {
    const wasRankedYesterday = yesterdayPos.has(row.artistId);
    const movement = wasRankedYesterday ? yesterdayPos.get(row.artistId)! - idx : undefined;

    const rebooksLast24h = all.filter(
      (b) =>
        b.artistId === row.artistId &&
        b.status === 'completed' &&
        b.checkedOutAt &&
        new Date(b.checkedOutAt).getTime() > yesterdayCutoff &&
        rebookedInChair(b),
    ).length;

    const badges: string[] = [];
    const firstRebookThisWeek = all.some(
      (b) =>
        b.artistId === row.artistId &&
        b.status === 'completed' &&
        b.checkedOutAt &&
        new Date(b.checkedOutAt).getTime() >= weekStart &&
        rebookedInChair(b),
    );
    if (firstRebookThisWeek && rebooksLast24h > 0 && row.rebooks === rebooksLast24h) {
      badges.push('First rebook this week');
    }
    if (row.streak >= 5) badges.push(`${row.streak}-streak`);

    return {
      artistId: row.artistId,
      name: row.name,
      studioId: row.studioId,
      rebooksThisMonth: row.rebooks,
      totalCompletedThisMonth: row.totalCompleted,
      streak: row.streak,
      badges,
      movement,
      rebooksLast24h,
    };
  });
}
